import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Calendar, Loader2, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import useSurcharge from "../hooks/useSurcharge";

const API = process.env.REACT_APP_BACKEND_URL;
const DEPOSIT_AMOUNT = 1000;

const TERMS_LINES = [
 "$1,000 non-refundable deposit secures your date.",
 "The deposit applies as credit toward your final cruise total.",
 "Balance due 14 days before the cruise. Bookings within 14 days require full payment.",
 "Public holiday charters carry a +20% surcharge with a 4-hour minimum.",
];

/**
 * Reusable date-reserve modal , opens a Stripe checkout for a $1,000 holding deposit.
 *
 * Props:
 *  - cruiseType: one of wedding|corporate|christmas|byo|hens|birthday|engagement|networking|private-charter
 *  - cruiseLabel: display label (e.g. "Wedding Cruise")
 *  - accent: tailwind color class for the primary button (e.g. "bg-rose-500 hover:bg-rose-400")
 *  - onClose: () => void
 */
export default function ReserveDateModal({ open, onClose, cruiseType, cruiseLabel, accent = "bg-slate-900 hover:bg-slate-800", initialDate = "" }) {
 const { apply } = useSurcharge();
 const { surcharge, total } = apply(DEPOSIT_AMOUNT);

 const [form, setForm] = useState({
  requested_date: initialDate,
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  guests: "",
  notes: "",
  accept_terms: false,
 });

 // Keep the date in sync when caller passes a new initialDate (e.g. from the
 // DateAvailabilityChecker that already validated the date).
 React.useEffect(() => {
  if (initialDate) setForm(f => ({ ...f, requested_date: initialDate }));
 }, [initialDate]);
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState("");

 if (!open) return null;

 const today = new Date().toISOString().slice(0, 10);
 const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError(""); };

 const canSubmit =
  form.requested_date &&
  form.customer_name.trim().length >= 2 &&
  /.+@.+\..+/.test(form.customer_email) &&
  form.accept_terms;

 const submit = async () => {
  setSubmitting(true);
  setError("");
  try {
   const payload = {
    cruise_type: cruiseType,
    requested_date: form.requested_date,
    customer_name: form.customer_name.trim(),
    customer_email: form.customer_email.trim(),
    customer_phone: form.customer_phone.trim() || undefined,
    guests: form.guests ? Number(form.guests) : undefined,
    notes: form.notes.trim() || undefined,
    accept_terms: form.accept_terms,
    origin_url: window.location.origin,
   };
   const { data } = await axios.post(`${API}/api/checkout/deposit`, payload);
   if (data?.url) {
    window.location.href = data.url;
   } else {
    throw new Error("No checkout URL returned");
   }
  } catch (e) {
   setError(e?.response?.data?.detail || e.message || "Something went wrong. Please try again.");
   setSubmitting(false);
  }
 };

 return createPortal(
  <div className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" data-testid="reserve-date-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
   <div className="bg-white w-full max-w-2xl rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[95vh] overflow-y-auto">
    {/* Header */}
    <div className="sticky top-0 bg-white px-6 sm:px-8 pt-6 pb-4 border-b border-slate-200 flex items-start justify-between gap-4 z-10">
     <div>
      <div className="text-[10px] uppercase tracking-[0.32em] font-black text-slate-500">Secure your date · 25% deposit</div>
      <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-1">{cruiseLabel}</h2>
     </div>
     <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0" data-testid="reserve-modal-close" aria-label="Close">
      <X className="w-4 h-4" />
     </button>
    </div>

    <div className="px-6 sm:px-8 py-6 space-y-5">
     {/* Pricing summary */}
     <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-5 py-4 flex items-baseline justify-between">
      <div>
       <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500">Holding deposit</div>
       <div className="text-3xl font-black text-slate-900 tabular-nums">${DEPOSIT_AMOUNT.toLocaleString()}</div>
      </div>
      <div className="text-right">
       <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500">+ Card fee 2%</div>
       <div className="text-sm font-bold text-slate-700 tabular-nums">+${surcharge.toFixed(2)}</div>
       <div className="text-xs text-slate-500 mt-1">Total <span className="font-black text-slate-900">${total.toFixed(2)} AUD</span></div>
      </div>
     </div>

     {/* Date */}
     <label className="block">
      <span className="text-[11px] uppercase tracking-[0.28em] font-black text-slate-700">Requested date *</span>
      <input type="date" min={today} value={form.requested_date} onChange={e => set("requested_date", e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl ring-1 ring-slate-300 focus:ring-slate-900 focus:outline-none text-base font-medium tabular-nums" data-testid="deposit-date" />
     </label>

     {/* Name + Email row */}
     <div className="grid sm:grid-cols-2 gap-4">
      <label className="block">
       <span className="text-[11px] uppercase tracking-[0.28em] font-black text-slate-700">Your name *</span>
       <input type="text" value={form.customer_name} onChange={e => set("customer_name", e.target.value)} placeholder="Sarah Johnson" className="mt-1.5 w-full px-4 py-3 rounded-xl ring-1 ring-slate-300 focus:ring-slate-900 focus:outline-none text-base" data-testid="deposit-name" />
      </label>
      <label className="block">
       <span className="text-[11px] uppercase tracking-[0.28em] font-black text-slate-700">Email *</span>
       <input type="email" value={form.customer_email} onChange={e => set("customer_email", e.target.value)} placeholder="sarah@email.com" className="mt-1.5 w-full px-4 py-3 rounded-xl ring-1 ring-slate-300 focus:ring-slate-900 focus:outline-none text-base" data-testid="deposit-email" />
      </label>
     </div>

     {/* Phone + Guests row */}
     <div className="grid sm:grid-cols-2 gap-4">
      <label className="block">
       <span className="text-[11px] uppercase tracking-[0.28em] font-black text-slate-700">Phone</span>
       <input type="tel" value={form.customer_phone} onChange={e => set("customer_phone", e.target.value)} placeholder="0400 000 000" className="mt-1.5 w-full px-4 py-3 rounded-xl ring-1 ring-slate-300 focus:ring-slate-900 focus:outline-none text-base" data-testid="deposit-phone" />
      </label>
      <label className="block">
       <span className="text-[11px] uppercase tracking-[0.28em] font-black text-slate-700">Approx. guests</span>
       <input type="number" min={1} max={120} value={form.guests} onChange={e => set("guests", e.target.value)} placeholder="80" className="mt-1.5 w-full px-4 py-3 rounded-xl ring-1 ring-slate-300 focus:ring-slate-900 focus:outline-none text-base" data-testid="deposit-guests" />
      </label>
     </div>

     {/* Notes */}
     <label className="block">
      <span className="text-[11px] uppercase tracking-[0.28em] font-black text-slate-700">Notes (optional)</span>
      <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Preferred boarding time, food preferences, any other details…" className="mt-1.5 w-full px-4 py-3 rounded-xl ring-1 ring-slate-300 focus:ring-slate-900 focus:outline-none text-base resize-none" data-testid="deposit-notes" />
     </label>

     {/* T&Cs */}
     <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 px-5 py-4">
      <div className="flex items-start gap-2.5 mb-2.5">
       <ShieldCheck className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
       <div className="text-[11px] uppercase tracking-[0.28em] font-black text-amber-900">Deposit Terms</div>
      </div>
      <ul className="space-y-1.5 text-sm text-amber-900/90 leading-snug">
       {TERMS_LINES.map((t, i) => <li key={i} className="flex gap-2"><span>·</span><span>{t}</span></li>)}
      </ul>
     </div>

     {/* Accept */}
     <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={form.accept_terms} onChange={e => set("accept_terms", e.target.checked)} className="w-5 h-5 mt-0.5 rounded accent-slate-900" data-testid="deposit-accept-terms" />
      <span className="text-sm text-slate-700 leading-snug">
       I have read and accept the deposit terms above. I understand my <strong className="text-slate-900">$1,000 deposit is non-refundable</strong> except where Magic Cruises cancels the cruise.
      </span>
     </label>

     {error && <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 text-rose-900 text-sm px-4 py-3" data-testid="deposit-error">{error}</div>}

     {/* CTA */}
     <button onClick={submit} disabled={!canSubmit || submitting} className={`w-full px-6 py-4 rounded-full text-white text-sm font-black uppercase tracking-[0.22em] shadow-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all ${accent}`} data-testid="deposit-submit">
      {submitting ? (
       <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</span>
      ) : (
       <span className="inline-flex items-center gap-2">Pay ${total.toFixed(2)} · Reserve Date <ArrowRight className="w-4 h-4" /></span>
      )}
     </button>

     <div className="text-center text-[10px] uppercase tracking-[0.28em] font-bold text-slate-400 inline-flex items-center justify-center gap-2 w-full"><Sparkles className="w-3 h-3" /> Secure payment by Stripe · 256-bit SSL</div>
    </div>
   </div>
  </div>,
  document.body
 );
}
